import { useState, useEffect } from 'react';
import { useNavigate }     from 'react-router-dom';
import { useTabsStore }    from '../store/tabsStore.js';
import { useCartStore }    from '../store/cartStore.js';
import { usingHub }        from '../lib/hub.js';
import { usarModo }        from '../store/deviceModeStore.js';
import { IcoTabs, IcoUser, IcoClose } from '../components/common/Icons.jsx';
import MoveTabSheet from '../components/MoveTabSheet.jsx';
import { useConfirmacion } from '../components/ConfirmSheet.jsx';
import { moveTab, mergeTab } from '../api/tabs.api.js';
import styles from './TabsScreen.module.css';

const fmt     = (n) => `$${Number(n ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
const fmtTime = (iso) => new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });

export default function TabsScreen() {
  const navigate      = useNavigate();
  const tabs          = useTabsStore((s) => s.tabs);
  const activeTabId   = useTabsStore((s) => s.activeTabId);
  // La comandera sin cuenta abierta empieza en el plano: mandarla a "/" la
  // rebotaba a /tables, y desde el plano sin mesas el único botón traía aquí.
  const inicio        = usarModo() === 'comandera' && !activeTabId ? '/tables' : '/';
  const openInCart    = useTabsStore((s) => s.openInCart);
  const closeTab      = useTabsStore((s) => s.closeTab);
  const load          = useTabsStore((s) => s.load);
  const loadErr       = useTabsStore((s) => s.error);
  const cartItems     = useCartStore((s) => s.items);

  // Sin caja central no hay WebSocket: se sondea para ver lo que abren las
  // demás tablets. Con caja, los cambios llegan solos.
  useEffect(() => {
    load();
    if (usingHub()) return;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const { pedir, Confirmacion } = useConfirmacion();
  const [moviendo, setMoviendo] = useState(null); // tab id

  const tabTotal = (tab) =>
    Math.max(0, tab.items.reduce((s, i) => s + i.total, 0) - (tab.discount ?? 0));

  const handleMove = async (tab, tableId) => {
    await moveTab(tab.id, tableId);
    await load();
  };

  // Juntar cierra una cuenta y engorda otra: recargar es más barato que
  // reconstruir a mano dos cuentas y sus comandas.
  const handleMerge = async (tab, intoId) => {
    await mergeTab(tab.id, intoId);
    await load();
  };

  const openTab = async (tab) => {
    if (cartItems.length > 0 && tab.id !== activeTabId) {
      if (!(await pedir({ titulo: `¿Abrir ${tab.name}?`, texto: 'Lo que hay en el carrito se reemplaza con esta cuenta.', confirmar: 'Abrir' }))) return;
    }
    openInCart(tab);
    navigate('/');
  };

  const handleDelete = (id) => {
    // Cerrar la cuenta en la caja libera la mesa en el plano de todas las tablets
    closeTab(id, { reason: 'deleted' }).catch(() => {});
    setConfirmDelete(null);
  };

  return (
    <div className={styles.root}>
      <div className={styles.inner}>

        <div className={styles.header}>
          <button className="btn btn--ghost" onClick={() => navigate(inicio)}>← Volver</button>
          <h1 className={styles.title}>Cuentas abiertas</h1>
        </div>

        {loadErr && <p style={{ color: 'var(--negative)', fontSize: 13 }}>{loadErr}</p>}

        {tabs.length === 0 ? (
          <div className={styles.empty}>
            <p className={styles.emptyIcon}><IcoTabs size={40} /></p>
            <p className={styles.emptyTitle}>Sin cuentas abiertas</p>
            <p className={styles.emptyText}>
              Guarda el carrito como cuenta para cobrar más tarde o manejar varias mesas a la vez.
            </p>
            <button className="btn btn--primary" style={{ marginTop: 16 }} onClick={() => navigate(inicio)}>
              {inicio === '/tables' ? 'Ir a las mesas' : 'Ir al catálogo'}
            </button>
          </div>
        ) : (
          <div className={styles.list}>
            {tabs.map((tab) => {
              const isActive  = activeTabId === tab.id;
              const itemCount = tab.items.reduce((s, i) => s + i.quantity, 0);
              return (
                <div key={tab.id} className={`${styles.card} ${isActive ? styles.cardActive : ''}`}>
                  <div className={styles.cardLeft}>
                    <div className={styles.cardName}>
                      {tab.name}
                      {isActive && <span className={styles.activePill}>Editando</span>}
                    </div>
                    <div className={styles.cardMeta}>
                      {itemCount} artículo{itemCount !== 1 ? 's' : ''} · {fmtTime(tab.createdAt)}
                      {tab.customer && <span> · <IcoUser size={12} /> {tab.customer.name}</span>}
                      {tab.openedByName && <span> · {tab.openedByName}</span>}
                    </div>
                    <div className={styles.cardItems}>
                      {tab.items.slice(0, 3).map((i) => i.product?.name ?? i.name).join(', ')}
                      {tab.items.length > 3 ? ` +${tab.items.length - 3}` : ''}
                    </div>
                  </div>
                  <div className={styles.cardRight}>
                    <strong className={styles.cardTotal}>{fmt(tabTotal(tab))}</strong>
                    <div className={styles.cardActions}>
                      <button
                        className="btn btn--primary"
                        style={{ fontSize: 14, padding: '0 16px', minHeight: 44 }}
                        onClick={() => openTab(tab)}
                      >
                        {isActive ? 'Continuar' : 'Abrir'}
                      </button>
                      <button
                        className="btn btn--ghost"
                        style={{ fontSize: 13, padding: '0 12px', minHeight: 44 }}
                        aria-label="Cambiar de mesa o juntar con otra cuenta"
                        onClick={() => setMoviendo(tab)}
                      >
                        Mover
                      </button>
                      {confirmDelete === tab.id ? (
                        <div className={styles.confirmRow}>
                          <button className="btn btn--danger" style={{ fontSize: 13, minHeight: 44 }} onClick={() => handleDelete(tab.id)}>Eliminar</button>
                          <button className="btn btn--ghost"  style={{ fontSize: 13, minHeight: 44 }} onClick={() => setConfirmDelete(null)}>Cancelar</button>
                        </div>
                      ) : (
                        <button
                          className="btn btn--ghost"
                          style={{ padding: '0 12px', minHeight: 44, minWidth: 44 }}
                          onClick={() => setConfirmDelete(tab.id)}
                          aria-label="Eliminar cuenta"
                        >
                          <IcoClose size={15} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <MoveTabSheet
        open={!!moviendo}
        tab={moviendo}
        tabs={tabs}
        onMove={handleMove}
        onMerge={handleMerge}
        onClose={() => setMoviendo(null)}
      />
      {Confirmacion}
    </div>
  );
}
